import { type ReactNode, useEffect } from 'react';

export interface ModalProps {
  /** Dialog heading. Also used as the dialog's accessible name. */
  title: string;
  /** Optional sub-heading under the title. */
  description?: string;
  onClose: () => void;
  children: ReactNode;
  /** Footer actions row (buttons). */
  footer?: ReactNode;
  /** Accessible label for the close button. Defaults to "Close". */
  closeLabel?: string;
}

/**
 * Modal — dialog shell shared across features (billing, staff, scheduling
 * confirmations): a bottom sheet on mobile, centered card from `sm:` up.
 * Presentational only: renders a scrim + card and forwards close events.
 * Closes on Escape and scrim click.
 */
export function Modal({ title, description, onClose, children, footer, closeLabel = 'Close' }: ModalProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-[110] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[calc(100dvh-1rem)] w-full max-w-[440px] flex-col overflow-hidden rounded-t-[20px] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-3)] sm:max-h-[calc(100dvh-3rem)] sm:rounded-[16px]"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--border)] px-[22px] pb-4 pt-[18px]">
          <div>
            <h2 className="text-[17px] font-bold text-[var(--text)]">{title}</h2>
            {description && <p className="mt-1 text-[13px] text-[var(--text-3)]">{description}</p>}
          </div>
          <button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[var(--text-3)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {children && <div className="min-h-0 overflow-y-auto px-[22px] py-4">{children}</div>}

        {footer && (
          <div className="flex shrink-0 gap-2.5 border-t border-[var(--border)] bg-[var(--surface)] px-[22px] pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">{footer}</div>
        )}
      </div>
    </div>
  );
}
